// Modules
import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";

// Environment variables
import { environment } from "../../../../environment";

// Components
import { DishResize } from "../room/resize/dish.layout";
import { ElectronGate } from "../../../electron";

// Properties
import { SelectorDevicesProperties } from "./selectorDevices.properties";
import { SelectorDeviceType } from "./selectorDevices.types";

// Styles
import "./selector.styles.scss";
import { useTranslation } from "react-i18next";
import { useElectron } from "../../../../hooks/useElectron";
import { findGlassesWindowCandidates, GlassesWindowCandidate } from "./glasses-window";
import { appEventEmitter } from "../../../../context/EventContext";

/**
 * Helper function to check if two labels match considering comma-separated parts
 * @param filterLabel The filter label to check
 * @param deviceLabel The device label to check against
 * @returns true if labels match
 */
const isLabelMatch = (filterLabel: string, deviceLabel: string): boolean => {
  if (!filterLabel || !deviceLabel) return false;

  // Split both labels by comma and trim each part
  const filterLabelParts = filterLabel.split(',').map(part => part.trim().toLowerCase());
  const deviceLabelParts = deviceLabel.split(',').map(part => part.trim().toLowerCase());

  // Check if any part of the filter label matches any part of the device label
  // or if the whole filter label is included in the device label
  return filterLabelParts.some(filterPart =>
    deviceLabelParts.some(devicePart => devicePart.includes(filterPart))
  ) || deviceLabel.toLowerCase().includes(filterLabel.toLowerCase());
};

/**
 * Selector Devices Component
 * @description Component to select devices
 * @param {SelectorDevicesProperties} properties
 * @returns {JSX.Element}
 */
export function SelectorDevicesComponent(
  properties: SelectorDevicesProperties
) {
  // translation function
  const { t } = useTranslation();

  // state to avoid multiple clicks in media selector
  const [selected, setSelected] = useState(false);

  // array of devices
  const [cameras, setCameras] = useState<SelectorDeviceType[]>([]);

  // búsqueda de ratón 3D en curso (estado visual del mando)
  const [searchingMouse, setSearchingMouse] = useState(false);

  // API de captura de ventanas de Electron, para compartir la vista de las gafas
  const { screenCapture } = useElectron();

  // Stream de la vista de las gafas mientras se está compartiendo
  const [glassesStream, setGlassesStream] = useState<MediaStream | null>(null);

  // Ventanas del escritorio que pueden estar espejando las gafas, ordenadas de
  // más a menos preferible. Pueden convivir varias: ML Hub 3, el Hub anterior y
  // scrcpy, y conviene saber cuál se va a compartir.
  const [glassesWindows, setGlassesWindows] = useState<GlassesWindowCandidate[]>([]);

  // get cameras from backend
  const getCameras = async () => {
    const groups = properties.groupsData;
    if (Array.isArray(groups) && groups.length > 0) {
      const allDevicesSet = new Map<string, SelectorDeviceType>();

      for (const group of groups) {
        for (const groups_devices of group.groups_devices) {
          allDevicesSet.set(groups_devices.device.id, groups_devices.device);
        }
      }

      // list of devices
      const camerasList = Array.from(allDevicesSet.values());
      // check is validateIsActive
      const camerasActives: any = [];
      for (const cameraSelect of camerasList) {
        // check if device is in properties.localScreams
        const active = await validateIsActive(cameraSelect);

        // device used state
        let used = false;

        if (cameraSelect?.filter?.label) {
          // search in local streams
          const localStream = properties.localScreams.find((stream) => {
            return stream.getTracks().find((track) => {
              if (!cameraSelect?.filter?.label) return false;
              return isLabelMatch(cameraSelect.filter.label, track.label);
            });
          });

          // if local stream exist, device is used
          if (localStream) used = true;
        }

        // add camera to list
        active &&
          camerasActives.push({
            ...cameraSelect,
            used: used,
          });
      }

      const finalCameras: SelectorDeviceType[] = [];

      const camerasIds = camerasActives.map((camera: SelectorDeviceType) => camera.id);

      // Filter out cameras that already have icons in the cache
      const camerasToFetch = camerasIds.filter((id: string) => !properties.devicesIconCache[id]);

      // Only fetch icons that aren't in the cache
      if (camerasToFetch.length > 0) {
        const camerasIconsResponses = await Promise.all(
          camerasToFetch.map((cameraId: string) =>
            axios.get(
              `${environment.api.host}/api/device/${cameraId}/icon`,
              {
                headers: {
                  Authorization: `Bearer ${properties.user.access_token}`,
                },
              }
            )
          )
        );

        // Create an object with the newly fetched icons
        const newIcons: Record<string, string> = {};
        camerasToFetch.forEach((id: string, index: number) => {
          newIcons[id] = camerasIconsResponses[index].data;
        });

        // Update the icon cache with new icons
        properties.setDevicesIconCache((prev: Record<string, string>) => ({
          ...prev,
          ...newIcons,
        }));

        // Assign icons from cache or from new fetches
        camerasActives.forEach((camera: SelectorDeviceType) => {
          finalCameras.push({
            ...camera,
            // Primero busca en los iconos recién obtenidos, luego en el caché
            icon: newIcons[camera.id] || properties.devicesIconCache[camera.id] || '',
          });
        });
      } else {
        // Assign icons from cache
        camerasActives.forEach((camera: SelectorDeviceType) => {
          finalCameras.push({
            ...camera,
            icon: properties.devicesIconCache[camera.id] || '',
          });
        });
      }

      // set cameras
      setCameras(finalCameras);
      properties.setHasDevicesBeenFetched(true);
    }
  };


  // get cameras on load
  useEffect(() => {
    getCameras();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.groupsData]);

  // validate if device is active
  const validateIsActive = async (camera: any) => {
    if ("label" in camera.filter) {
      // get all enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // search device with label - handle comma-separated labels better
      const device = devices.find((device) => {
        if (!("label" in camera.filter)) return false;
        return isLabelMatch(camera.filter.label, device.label);
      });

      // if device exist, return true
      if (device) return device;
    } else {
      // get stream directly with getUserMedia for validation
      try {
        const stream = await navigator.mediaDevices.getUserMedia(camera.filter);
        if (stream) {
          // Stop the stream immediately since it's just for validation
          stream.getTracks().forEach(track => track.stop());
          return stream;
        }
      } catch (error) {
        console.warn('Device validation failed:', error);
      }
    }
    return undefined;
  };

  /**
   * Busca entre las ventanas del escritorio las que pueden estar espejando las
   * gafas. Devuelve todas las encontradas, la preferida primero.
   */
  const findGlassesWindows = useCallback(async (): Promise<GlassesWindowCandidate[]> => {
    if (!screenCapture) return [];

    try {
      const sources = await screenCapture.getSources();
      return findGlassesWindowCandidates(sources);
    } catch (error) {
      console.warn('[VR] No se pudieron listar las ventanas del escritorio:', error);
      return [];
    }
  }, [screenCapture]);

  /**
   * Sondea periódicamente si la ventana espejo está abierta, para que el botón
   * de compartir refleje si hay algo que compartir sin que el usuario pruebe.
   */
  useEffect(() => {
    if (!properties.isMagicLeapConnected || !screenCapture) {
      setGlassesWindows([]);
      return;
    }

    let cancelled = false;
    let anterior = '';

    const scan = async () => {
      const found = await findGlassesWindows();
      if (cancelled) return;

      setGlassesWindows(found);

      // Se deja constancia de lo encontrado solo cuando cambia, para que quede
      // claro cuál se comparte si hay varias herramientas abiertas
      const huella = found.map((w) => w.id).join('|');
      if (huella !== anterior) {
        anterior = huella;
        if (found.length > 1) {
          console.log(
            `[VR] ${found.length} ventanas candidatas; se usará "${found[0].name}" (${found[0].tool}):`,
            found.map((w) => `${w.name} [${w.tool}]`),
          );
        } else if (found.length === 1) {
          console.log(`[VR] Ventana espejo: "${found[0].name}" (${found[0].tool})`);
        }
      }
    };

    scan();
    const interval = setInterval(scan, 4000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [properties.isMagicLeapConnected, screenCapture, findGlassesWindows]);

  /**
   * Comparte la vista de las gafas como un stream más de la sala.
   *
   * Captura la ventana espejo con el mismo mecanismo que ya se usa para
   * compartir aplicaciones nativas (`chromeMediaSource: 'desktop'`), de modo
   * que el vídeo entra en el pipeline WebRTC existente sin ningún tratamiento
   * especial: para el resto de la sala es un dispositivo médico más.
   */
  const shareGlassesView = async () => {
    const target = glassesWindows[0] ?? (await findGlassesWindows())[0];

    if (!target) {
      toast.warn(t('spacemouse.share.no_window'));
      return;
    }

    console.log(`[VR] Compartiendo "${target.name}" (${target.tool})`);

    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          // @ts-ignore — constraints propias de Electron/Chromium
          mandatory: {
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: target.id,
            minWidth: 1280,
            maxWidth: 1920,
            minHeight: 720,
            maxHeight: 1080,
          },
        },
      });

      setGlassesStream(media);
      properties.addStream?.(media, { type: 'med.device' }, { manual_id: '' });

      await appEventEmitter.emit('device:started', {
        action: 'start',
        resource: 'device',
        agent: media.id,
        event_phase: 'start',
        user: properties.user,
        device: 'magic-leap-2-view',
        room: properties.room,
      });

      toast.success(t('spacemouse.share.started.named', { window: target.name }));
    } catch (error) {
      console.error('[VR] Error capturando la vista de las gafas:', error);
      toast.error(t('spacemouse.share.error'));
    }
  };

  /** Deja de compartir la vista de las gafas */
  const stopSharingGlassesView = async () => {
    if (!glassesStream) return;

    const index = properties.localScreams.findIndex((s) => s.id === glassesStream.id);
    if (index >= 0) properties.removeStream?.(index);

    glassesStream.getTracks().forEach((track) => track.stop());
    setGlassesStream(null);

    await appEventEmitter.emit('device:stopped', {
      action: 'stop',
      resource: 'device',
      agent: glassesStream.id,
      event_phase: 'end',
      user: properties.user,
      device: 'magic-leap-2-view',
      room: properties.room,
    });
  };

  /**
   * Tarjeta de realidad virtual (Magic Leap 2 + ratón 3D).
   *
   * A diferencia del resto de dispositivos, no se pinta a partir de
   * `/api/group/me`: las gafas no aparecen en `enumerateDevices()` porque se
   * presentan como clase USB 6 (Still Imaging), no como cámara ni micrófono.
   * Por eso su estado viene de la detección WebUSB de Electron y de la
   * presencia en la sala del ratón 3D, y no depende del cliente ni de que el
   * dispositivo esté dado de alta en base de datos.
   *
   * Reutiliza el layout del resto de tarjetas: las gafas ocupan el hueco de la
   * imagen del dispositivo y el mando el del botón de acción. El mando indica
   * el estado del vínculo con el ratón 3D por color:
   *
   *   verde   → vinculado
   *   gris    → sin vincular; al pulsarlo busca un ratón en la sala
   *   rojo    → el vínculo existió y se ha perdido, con el motivo debajo
   *   ámbar   → buscando
   *
   * Solo se muestra si hay algo que enseñar: gafas conectadas aquí, o un ratón
   * 3D presente en la sala.
   */
  const renderVirtualRealityDevice = () => {
    const glassesConnected = !!properties.isMagicLeapConnected;
    const mouseInRoom = !!properties.hasSpaceMouseInRoom;

    if (!glassesConnected && !mouseInRoom) return null;

    // El estado 'searching' es local: dura lo que tarda la respuesta del servidor
    const status: 'linked' | 'lost' | 'searching' | 'idle' =
      searchingMouse ? 'searching' : (properties.spaceMouseStatus ?? 'idle');

    const statusLabel = {
      linked: t('spacemouse.status.linked'),
      lost: t('spacemouse.status.lost'),
      searching: t('spacemouse.status.searching'),
      idle: mouseInRoom ? t('spacemouse.status.ready') : t('spacemouse.status.waiting_mouse'),
    }[status];

    // Solo tiene sentido buscar ratón si este equipo aporta las gafas
    const canSearch = glassesConnected && status !== 'searching';

    // Compartir la vista solo tiene sentido con las gafas de este equipo
    const isSharing = !!glassesStream;
    const ventana = glassesWindows[0];
    const canShare = glassesConnected && (isSharing || !!ventana);

    const shareTitle = isSharing
      ? t('spacemouse.share.stop')
      : ventana
        ? (glassesWindows.length > 1
            ? t('spacemouse.share.start.many', { window: ventana.name, count: glassesWindows.length })
            : t('spacemouse.share.start.named', { window: ventana.name }))
        : t('spacemouse.share.no_window');

    /**
     * Pulsar el mando busca un ratón 3D en la sala e intenta vincular.
     * Si ya está vinculado, deshace el vínculo.
     */
    const onGamepadClick = () => {
      if (status === 'linked') {
        properties.onUnlinkSpaceMouse?.();
        return;
      }

      if (!canSearch) return;

      setSearchingMouse(true);
      properties.onLinkSpaceMouse?.();

      // El resultado llega por socket; si no llega nada, dejamos de buscar
      window.setTimeout(() => setSearchingMouse(false), 2500);
    };

    return (
      <ElectronGate>
        <div className="camera vr-card" key="virtual-reality">
          <div className="name">{t('spacemouse.device.name')}</div>

          <div className={`vr-status is-${status}`}>
            {statusLabel}
            {status === 'lost' && properties.spaceMouseStatusDetail && (
              <span className="vr-status-detail">{properties.spaceMouseStatusDetail}</span>
            )}
          </div>

          {/* Gafas: identifica el dispositivo, arriba a la derecha */}
          <i
            className={`las la-vr-cardboard vr-glasses ${glassesConnected ? 'is-on' : 'is-off'}`}
            title={String(t('spacemouse.device.glasses'))}
          />

          {/* Acciones: una sola fila, mismo tamaño y separación uniforme */}
          <div className="vr-actions">
            {/* Mando: estado del vínculo con el ratón 3D y búsqueda */}
            <button
              type="button"
              className={`vr-btn is-${status}`}
              title={String(statusLabel)}
              aria-label={String(statusLabel)}
              onClick={onGamepadClick}
            >
              <i className="las la-gamepad" />
            </button>

            {/* Compartir lo que ve el usuario en las gafas */}
            {glassesConnected && (
              <button
                type="button"
                className={`vr-btn ${isSharing ? 'is-sharing' : canShare ? 'is-ready' : 'is-off'}`}
                title={String(shareTitle)}
                aria-label={String(shareTitle)}
                onClick={() => (isSharing ? stopSharingGlassesView() : shareGlassesView())}
              >
                <i className={`las ${isSharing ? 'la-stop-circle' : 'la-video'}`} />
              </button>
            )}

            {/* Unir las gafas a la sala escaneando un QR */}
            {glassesConnected && (
              <button
                type="button"
                className="vr-btn is-pair"
                title={String(t('pairing.open'))}
                aria-label={String(t('pairing.open'))}
                onClick={() => properties.onOpenPairing?.()}
              >
                <i className="las la-qrcode" />
              </button>
            )}
          </div>
        </div>
      </ElectronGate>
    );
  };

  return (
    <>
      {properties.canBeDisplayed && renderVirtualRealityDevice()}
      {properties.canBeDisplayed &&
        cameras.map((camera) => {
          return (
            <div
              className="camera"
              style={{
                backgroundColor: camera.used ? "#b7bffc" : "#fff",
                borderStyle: "solid",
                borderColor: camera.used ? "#2f49ff" : "#fff",
              }}
              key={camera.id}
              onClick={async () => {
                // avoid multiple clicks
                if (selected) return;

                // set selected state
                setSelected(true);

                if ("label" in camera.filter) {
                  // search again in enumerate devices
                  const devices = await navigator.mediaDevices.enumerateDevices();

                  // label contain camera.filter.label - handle comma-separated labels
                  const device = devices.find((device) => {
                    if (!("label" in camera.filter)) return false;
                    return isLabelMatch(camera.filter.label, device.label);
                  });

                  if (device) {
                    // Check if device is being used
                    const used = properties.localScreams.find((stream) => {
                      return stream.getTracks().find((track) => {
                        return isLabelMatch(device.label, track.label);
                      });
                    });

                    if (used) {
                      properties.removeStream?.(
                        properties.localScreams.indexOf(used)
                      );

                      await appEventEmitter.emit("device:stopped", {
                        action: "stop",
                        resource: "device",
                        agent: used.id,
                        event_phase: "end",
                        user: properties.user,
                        device: `${camera.name}-${camera.model}`,
                        room: properties.room,
                      });

                      setSelected(false);
                      properties.setScene?.(undefined);
                      return;
                    }

                    // Todos los dispositivos en este selector son médicos
                    // Usar configuración médica optimizada para preservar frecuencias bajas
                    let stream: MediaStream | undefined;

                    if (camera.filter.audio && !camera.filter.video) {
                      // Dispositivo solo de audio (estetoscopio), usar configuración médica
                      console.log('🩺 Obteniendo stream médico de audio para:', device.label);
                      stream = await properties.deviceInstance.getMedicalDeviceStream(
                        device.deviceId,
                        {
                          preserveLowFrequencies: true,
                          highQualityAudio: true,
                          disableProcessing: true,
                          sampleRate: 48000
                        }
                      );
                    } else {
                      // Dispositivo médico con video o mixto, aplicar configuración médica optimizada
                      console.log('🏥 Obteniendo stream médico con video para:', device.label);
                      stream = await navigator.mediaDevices.getUserMedia({
                        video: camera.filter.video
                          ? { deviceId: { exact: device.deviceId } }
                          : false,
                        audio: camera.filter.audio
                          ? { deviceId: { exact: device.deviceId } }
                          : false,
                      });
                    }

                    const manualId = camera.manual_id ?? "";

                    // add stream
                    if (stream) {
                      properties.addStream?.(
                        stream,
                        { type: "med.device" },
                        { manual_id: manualId }
                      );

                      await appEventEmitter.emit("device:started", {
                        action: "start",
                        resource: "device",
                        agent: stream.id,
                        event_phase: "start",
                        user: properties.user,
                        device: `${camera.name}-${camera.model}`,
                      });
                    }
                  }
                } else {
                  // Dispositivo médico con filtro personalizado
                  console.log('🏥 Obteniendo stream médico con filtro personalizado para:', camera.name);

                  // Aplicar configuración médica si el filtro incluye audio
                  let medicalFilter = { ...camera.filter };
                  if (medicalFilter.audio && typeof medicalFilter.audio === 'object') {
                    medicalFilter.audio = {
                      ...medicalFilter.audio,
                      // Configuración médica para preservar frecuencias bajas
                      echoCancellation: false,
                      noiseSuppression: false,
                      autoGainControl: false,
                      sampleRate: 48000,
                      // @ts-ignore - Propiedades experimentales para dispositivos médicos
                      googEchoCancellation: false,
                      googNoiseSuppression: false,
                      googAutoGainControl: false,
                      googHighpassFilter: false,
                    };
                  } else if (medicalFilter.audio === true) {
                    medicalFilter.audio = {
                      echoCancellation: false,
                      noiseSuppression: false,
                      autoGainControl: false,
                      sampleRate: 48000,
                      // @ts-ignore - Propiedades experimentales para dispositivos médicos
                      googEchoCancellation: false,
                      googNoiseSuppression: false,
                      googAutoGainControl: false,
                      googHighpassFilter: false,
                    };
                  }

                  const stream = await navigator.mediaDevices.getUserMedia(medicalFilter);
                  if (stream)
                    properties.addStream?.(
                      stream,
                      { type: "med.device" },
                      { manual_id: camera.manual_id ?? "" }
                    );
                }

                // resize dish cameras
                new DishResize(
                  "DishScenary",
                  "DishScenary",
                  "DishVideoComponent"
                ).resize();

                // close selector
                properties.setScene?.(undefined);
              }}
            >
              {
                // hide add icon if device is used
                <i className={camera.used ? "las la-times" : "las la-plus"}></i>
              }
              <div className="name">
                {camera.name}
              </div>
              <div
                className="image"
                style={{
                  backgroundColor: camera.used ? "#b7bffc" : "#fff",
                  backgroundImage: camera.icon ? `url(${camera.icon})` : "none",
                }}
              />
            </div>
          );
        })}
      {cameras.length === 0 && !properties.isMagicLeapConnected && !properties.hasSpaceMouseInRoom && (
        <div
          className="no-camera"
          style={{
            color: "#fff",
            flex: 1,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {t("selector.empty")}
        </div>
      )}
    </>
  );
}
